// Formül yardımı penceresi — fonksiyonlar, operatörler, kurallar ve tıklayınca ifadeye eklenen
// örnekler (içerik tek kaynak: lib/kpi-formula-help.ts). FormulaEditor "Formül yardımı" ile açar.

import { useEffect } from "react";
import {
  FORMULA_DIMENSION_COPY,
  FORMULA_HELP_RULES,
  FORMULA_HELP_SECTIONS,
  formulaDimensionChips,
  type FormulaDimensionChip,
} from "../../lib/kpi-formula-help";
import { useKpiBreakdownDimensionsStore } from "../../stores/kpi-breakdown-dimensions-store";
import { KpiModal } from "./KpiModal";
import { btnGhost, btnSmall, monoStyle } from "./kpi-form-styles";

export interface FormulaHelpPanelProps {
  open: boolean;
  onClose: () => void;
  /** örneği ifadeye ekler (son odaklanan varyantın imlecine); yoksa düğme gizlenir */
  onInsert?: ((example: string) => void) | undefined;
  /** göstergenin kendi kırılım grupları (boyut slug + ad) — çip olarak sunulur (TODO-KPI-154) */
  ownDimensions?: readonly { slug: string; label: string }[] | undefined;
}

/** Boyut çipleri bölümü: göstergenin grupları + özel boyutlar; hiçbiri yoksa görünmez. */
function DimensionChips(props: {
  ownDimensions: readonly { slug: string; label: string }[];
  onInsert: ((example: string) => void) | undefined;
}): JSX.Element | null {
  const custom = useKpiBreakdownDimensionsStore((s) => s.custom);
  const load = useKpiBreakdownDimensionsStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  const chips = formulaDimensionChips(props.ownDimensions, custom);
  if (chips.own.length + chips.custom.length === 0) return null;
  const C = FORMULA_DIMENSION_COPY;
  const group = (title: string, list: FormulaDimensionChip[]): JSX.Element | null =>
    list.length === 0 ? null : (
      <div className="flex items-center gap-2 flex-wrap" key={title}>
        <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {title}:
        </span>
        {list.map((c) =>
          props.onInsert ? (
            <button
              key={c.slug}
              type="button"
              style={{ ...btnSmall, minHeight: 36 }}
              title={C.chipTitle(c.call)}
              data-testid="kpi-formula-dimension-chip"
              onClick={() => props.onInsert?.(c.call)}
            >
              {c.label} <code style={monoStyle}>{c.slug}</code>
            </button>
          ) : (
            <span key={c.slug} className="text-sm" data-testid="kpi-formula-dimension-chip">
              {c.label} <code style={monoStyle}>{c.slug}</code>
            </span>
          ),
        )}
      </div>
    );
  return (
    <section data-testid="kpi-formula-dimensions">
      <h3
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-3)",
          margin: "0 0 8px",
        }}
      >
        {C.title}
      </h3>
      <p className="text-sm" style={{ color: "var(--color-text-2)", margin: "0 0 8px" }}>
        {C.intro}
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {group(C.own, chips.own)}
        {group(C.custom, chips.custom)}
      </div>
    </section>
  );
}

export function FormulaHelpPanel({
  open,
  onClose,
  onInsert,
  ownDimensions,
}: FormulaHelpPanelProps): JSX.Element | null {
  return (
    <KpiModal
      open={open}
      kicker="Formül dili"
      title="Formül yardımı"
      description="Gösterge kodları, dört işlem ve aşağıdaki fonksiyonlarla yazılır. Örneğe tıklayın: ifadeye eklenir."
      width={820}
      onClose={onClose}
      footer={
        <button type="button" style={btnGhost} onClick={onClose}>
          Kapat
        </button>
      }
    >
      <div
        style={{ display: "grid", gap: 18, maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}
      >
        {FORMULA_HELP_SECTIONS.map((sec) => (
          <section key={sec.title}>
            <h3
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--color-text-3)",
                margin: "0 0 8px",
              }}
            >
              {sec.title}
            </h3>
            <div style={{ display: "grid", gap: 8 }}>
              {sec.items.map((it) => (
                <div
                  key={it.signature}
                  className="rounded-lg p-3"
                  style={{ border: "1px solid var(--color-border)", display: "grid", gap: 6 }}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <code style={{ ...monoStyle, fontWeight: 700, color: "var(--color-text)" }}>
                      {it.signature}
                    </code>
                    <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
                      {it.title}
                    </span>
                  </div>
                  <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
                    {it.description}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code
                      style={{
                        ...monoStyle,
                        background: "var(--color-bg)",
                        padding: "4px 8px",
                        borderRadius: 6,
                        color: "var(--color-text)",
                      }}
                    >
                      {it.example}
                    </code>
                    {it.exampleNote ? (
                      <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        {it.exampleNote}
                      </span>
                    ) : null}
                    {it.excel ? (
                      <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        Excel: <code style={monoStyle}>{it.excel}</code>
                      </span>
                    ) : null}
                    {onInsert ? (
                      <button
                        type="button"
                        style={{ ...btnSmall, marginLeft: "auto" }}
                        onClick={() => onInsert(it.example)}
                      >
                        Ekle
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        <DimensionChips ownDimensions={ownDimensions ?? []} onInsert={onInsert} />
        <section>
          <h3
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--color-text-3)",
              margin: "0 0 8px",
            }}
          >
            Kurallar
          </h3>
          <ul
            className="text-sm"
            style={{ color: "var(--color-text-2)", paddingLeft: 18, margin: 0 }}
          >
            {FORMULA_HELP_RULES.map((r) => (
              <li key={r} style={{ marginBottom: 4 }}>
                {r}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </KpiModal>
  );
}
